// Cold-room temperature logbook (R-A7) - the pure rules.
//
// PPECB regs 2024 reg 29(1)(e): a cold store keeps a temperature record. The
// rows live in coldroom_temp_readings (mig 20261128000000): append-only, a
// mistake is fixed by a NEW row whose supersedes_id names the old one.
//
// The day is SAST (UTC+2, no DST). A room owed N checks a day has N equal
// windows of 24/N hours from 00:00 SAST (2 a day: 00:00-12:00 and 12:00-24:00).
// A reading counts for the window its read_at falls in.
import { SAST_OFFSET_MS } from '@/lib/dates'

export interface TempRoom {
  coldroom_id: string
  code: string
  name: string
  is_active: boolean
  temp_min_c: number | null
  temp_max_c: number | null
  readings_per_day: number | null
}

export interface TempReading {
  reading_id: string
  coldroom_id: string
  read_at: string
  air_temp_c: number
  pulp_temp_c: number | null
  note: string | null
  recorded_by: string
  supersedes_id: string | null
}

/** The checks a room is owed when the setup says nothing (ruled 22 Oct). */
export const DEFAULT_READINGS_PER_DAY = 2

export const TEMP_MIN_LIMIT = -40
export const TEMP_MAX_LIMIT = 60

/** Keep digits, one point (a comma counts as a point) and a leading minus. */
export function sanitizeTempInput(v: string): string {
  const s = v.replace(',', '.')
  const neg = s.trimStart().startsWith('-')
  const body = s.replace(/[^0-9.]/g, '')
  const i = body.indexOf('.')
  const one = i === -1 ? body : body.slice(0, i + 1) + body.slice(i + 1).replace(/\./g, '')
  return (neg ? '-' : '') + one
}

/** A typed temperature as a number, or null when it is blank or not a number. */
export function parseTemp(v: string): number | null {
  const t = v.trim()
  if (t === '' || t === '-' || t === '.' || t === '-.') return null
  const n = Number(t)
  if (!Number.isFinite(n)) return null
  return Math.round(n * 10) / 10
}

export function tempInLimits(n: number): boolean {
  return n >= TEMP_MIN_LIMIT && n <= TEMP_MAX_LIMIT
}

export type RangeStatus = 'no_range' | 'in_range' | 'too_cold' | 'too_warm'

/** Where an air reading sits against the room's range. A room with neither end
 *  set is never flagged: no range set = no flag. */
export function rangeStatus(air: number, min: number | null, max: number | null): RangeStatus {
  if (min == null && max == null) return 'no_range'
  if (min != null && air < min) return 'too_cold'
  if (max != null && air > max) return 'too_warm'
  return 'in_range'
}

export function hasRange(room: Pick<TempRoom, 'temp_min_c' | 'temp_max_c'>): boolean {
  return room.temp_min_c != null || room.temp_max_c != null
}

/** "1.5 to 4.5 C", "at most 4.5 C", "at least 1.5 C", or null. */
export function rangeText(min: number | null, max: number | null): string | null {
  if (min != null && max != null) return `${fmtTemp(min)} to ${fmtTemp(max)} °C`
  if (max != null) return `up to ${fmtTemp(max)} °C`
  if (min != null) return `from ${fmtTemp(min)} °C`
  return null
}

export function fmtTemp(n: number | null | undefined): string {
  return n == null ? '—' : n.toFixed(1)
}

/** Checks owed per day for a room; a blank setup means the default. */
export function checksPerDay(room: Pick<TempRoom, 'readings_per_day'>): number {
  const n = room.readings_per_day
  return n == null ? DEFAULT_READINGS_PER_DAY : n
}

function hhmm(h: number): string {
  const total = Math.round(h * 60)
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

export interface Slot { index: number; startH: number; endH: number; label: string }

/** N equal windows across the SAST day. */
export function slotWindows(n: number): Slot[] {
  if (!Number.isInteger(n) || n <= 0) return []
  const w = 24 / n
  return Array.from({ length: n }, (_, i) => ({
    index: i, startH: i * w, endH: (i + 1) * w,
    label: `${hhmm(i * w)}–${i === n - 1 ? '24:00' : hhmm((i + 1) * w)}`,
  }))
}

/** ms of 00:00 SAST on a YYYY-MM-DD. */
function sastDayStartMs(ymd: string): number {
  return Date.parse(`${ymd}T00:00:00Z`) - SAST_OFFSET_MS
}

export function sastDayRange(ymd: string): { from: string; to: string } {
  const s = sastDayStartMs(ymd)
  return { from: new Date(s).toISOString(), to: new Date(s + 86_400_000).toISOString() }
}

/** First instant AFTER the last day of a range (the exclusive upper bound). */
export function sastRangeBounds(fromYmd: string, toYmd: string): { from: string; to: string } {
  return { from: sastDayRange(fromYmd).from, to: sastDayRange(toYmd).to }
}

/** Which window of the SAST day `ymd` an instant falls in, or null when it is
 *  not on that day. */
export function slotIndexOf(readAt: string, ymd: string, n: number): number | null {
  const t = Date.parse(readAt)
  const start = sastDayStartMs(ymd)
  if (!Number.isFinite(t) || t < start || t >= start + 86_400_000 || n <= 0) return null
  return Math.min(n - 1, Math.floor(((t - start) / 86_400_000) * n))
}

/** The readings still standing: a reading another row supersedes is dropped. */
export function effectiveReadings<T extends { reading_id: string; supersedes_id: string | null }>(rows: T[]): T[] {
  const gone = new Set(rows.map(r => r.supersedes_id).filter((x): x is string => !!x))
  return rows.filter(r => !gone.has(r.reading_id))
}

export type SlotState = 'done' | 'missing' | 'due' | 'later'
export interface GridSlot extends Slot { state: SlotState; reading: TempReading | null }
export interface GridRow { room: TempRoom; owed: number; slots: GridSlot[]; missing: number }

/** Today's grid: rooms x check windows. A window with a reading is `done`; one
 *  already over with none is `missing`; the one running now with none is `due`;
 *  a later one is `later`. Inactive rooms and rooms owed 0 checks are left out. */
export function buildGrid(rooms: TempRoom[], readings: TempReading[], ymd: string, nowMs: number): GridRow[] {
  const standing = effectiveReadings(readings)
  const start = sastDayStartMs(ymd)
  const rows: GridRow[] = []
  for (const room of rooms) {
    if (!room.is_active) continue
    const owed = checksPerDay(room)
    if (owed <= 0) continue
    const mine = standing.filter(r => r.coldroom_id === room.coldroom_id)
    const slots: GridSlot[] = slotWindows(owed).map(s => {
      const hit = mine
        .filter(r => slotIndexOf(r.read_at, ymd, owed) === s.index)
        .sort((a, b) => Date.parse(b.read_at) - Date.parse(a.read_at))[0] ?? null
      const sStart = start + s.startH * 3_600_000
      const sEnd = start + s.endH * 3_600_000
      const state: SlotState = hit ? 'done' : nowMs >= sEnd ? 'missing' : nowMs >= sStart ? 'due' : 'later'
      return { ...s, state, reading: hit }
    })
    rows.push({ room, owed, slots, missing: slots.filter(s => s.state === 'missing').length })
  }
  return rows
}

export function shiftYmd(ymd: string, days: number): string {
  return new Date(Date.parse(`${ymd}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10)
}

/** The Mark of a log line for the printed sheet: which readings were replaced. */
export function supersededIds(rows: { reading_id: string; supersedes_id: string | null }[]): Set<string> {
  return new Set(rows.map(r => r.supersedes_id).filter((x): x is string => !!x))
}

export interface RoomTempSetup {
  temp_min_c: number | null
  temp_max_c: number | null
  readings_per_day: number | null
}

/** The three room-setup fields, typed as text ('' = not set), to what is saved.
 *  A blank range is saved as NULL (no flag); a blank checks-per-day as NULL (the
 *  default of 2 a day applies). */
export function parseRoomTempSetup(min: string, max: string, perDay: string):
  { ok: true; value: RoomTempSetup } | { ok: false; error: string } {
  const lo = parseTemp(min), hi = parseTemp(max)
  if (min.trim() !== '' && lo == null) return { ok: false, error: 'Lowest temperature must be a number (e.g. 1.5)' }
  if (max.trim() !== '' && hi == null) return { ok: false, error: 'Highest temperature must be a number (e.g. 4.5)' }
  if ((lo != null && !tempInLimits(lo)) || (hi != null && !tempInLimits(hi))) {
    return { ok: false, error: `Temperatures must be between ${TEMP_MIN_LIMIT} and ${TEMP_MAX_LIMIT} °C` }
  }
  if (lo != null && hi != null && lo > hi) return { ok: false, error: 'Lowest temperature cannot be above the highest' }
  const t = perDay.trim()
  const n = t === '' ? null : Number(t)
  if (n != null && (!Number.isInteger(n) || n < 0 || n > 24)) {
    return { ok: false, error: 'Checks per day must be a whole number from 0 to 24 (or blank)' }
  }
  return { ok: true, value: { temp_min_c: lo, temp_max_c: hi, readings_per_day: n } }
}
